import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { SUPPORTED_LOCALES, FALLBACK_LOCALE, resolveLocale } from '@powerful/config/i18n';
import type { SupportedLocale } from '@powerful/config/i18n';

import ptCommon from './locales/pt-PT/common.json';
import ptErrors from './locales/pt-PT/errors.json';
import enCommon from './locales/en/common.json';
import enErrors from './locales/en/errors.json';
import esCommon from './locales/es/common.json';
import esErrors from './locales/es/errors.json';
import frCommon from './locales/fr/common.json';
import frErrors from './locales/fr/errors.json';

/** Chave de persistência da escolha explícita do usuário (§4.2 do PRD). */
export const LOCALE_STORAGE_KEY = 'powerful-locale';

export const NAMESPACES = ['common', 'errors'] as const;

// Bundles importados estaticamente, não por HTTP: o app roda em WebView do Capacitor e um
// backend de carregamento assíncrono adicionaria um estado de "traduções a caminho" na
// primeira tela. Com 4 locales pequenos (<25 KB gzip somados), o custo de embutir é menor.
const resources = {
  'pt-PT': { common: ptCommon, errors: ptErrors },
  en: { common: enCommon, errors: enErrors },
  es: { common: esCommon, errors: esErrors },
  fr: { common: frCommon, errors: frErrors },
} as const;

/**
 * Lê a escolha explícita do usuário, se houver.
 * Em try/catch: modo privado e navegadores com site data bloqueada lançam no acesso.
 */
function storedLocale(): SupportedLocale | undefined {
  try {
    const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return saved ? resolveLocale(saved) : undefined;
  } catch {
    return undefined;
  }
}

export function initI18n(): typeof i18n {
  if (i18n.isInitialized) return i18n;

  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      // A escolha explícita sobrepõe a detecção; sem escolha, o detector decide e o
      // resolveLocale normaliza o que vier do dispositivo (ex.: pt-BR → pt-PT).
      lng: storedLocale(),
      fallbackLng: FALLBACK_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      ns: [...NAMESPACES],
      defaultNS: 'common',
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: LOCALE_STORAGE_KEY,
        caches: [],
      },
      interpolation: { escapeValue: false },
      returnNull: false,
    });

  return i18n;
}

/** Troca o idioma em runtime e persiste a escolha. Não recarrega a aplicação. */
export async function changeLocale(raw: string): Promise<SupportedLocale> {
  const locale = resolveLocale(raw);
  await i18n.changeLanguage(locale);
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Persistência é conveniência: a sessão continua no idioma escolhido.
  }
  return locale;
}

export { i18n };
